import type { Column } from "@replo/design-system/components/table/Table";
import type {
  ReploElementMetadata,
  ReploElementType,
} from "schemas/generated/element";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { useCreateElement } from "@editor/hooks/element/useCreateElement";
import { useDuplicateElement } from "@editor/hooks/element/useDuplicateElement";
import useListAllElementsMetadata from "@editor/hooks/element/useListAllElementsMetadata";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useEditedTimeString } from "@editor/hooks/useEditedTimeString";
import { useOpenModal } from "@editor/hooks/useModal";
import { routes } from "@editor/utils/router";

import AnnouncementBanner from "@/features/announcements/AnnouncementBanner";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import SplitButton from "@replo/design-system/components/button/SplitButton";
import SelectableChip from "@replo/design-system/components/chip/SelectableChip";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { Menu } from "@replo/design-system/components/menu/Menu";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import PageHeader from "@replo/design-system/components/PageHeader";
import Popover from "@replo/design-system/components/popover/Popover";
import Separator from "@replo/design-system/components/Separator";
import Table from "@replo/design-system/components/table/Table";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  AlignVerticalSpaceAround,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  File,
  LayoutTemplate,
  MoreHorizontal,
  Newspaper,
  Plus,
  Search,
  ShoppingBag,
} from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import PageCard from "./PageCard";

type SortBy = "name" | "createdAt" | "updatedAt";
type SortOrder = "asc" | "desc";

const sortByOptions = [
  { label: "Alphabetical", value: "name" },
  { label: "Date Created", value: "createdAt" },
  { label: "Last Modified", value: "updatedAt" },
];

const sortOrderOptions = [
  {
    label: "A to Z",
    altLabel: "Oldest First",
    value: "asc",
    startEnhancer: <ArrowUp size={14} />,
  },
  {
    label: "Z to A",
    altLabel: "Newest First",
    value: "desc",
    startEnhancer: <ArrowDown size={14} />,
  },
];

type ViewMode = "gallery" | "list";

const useFilteredPages = (
  searchTerm: string,
  selectedFilter: ReploElementType | "all",
  sortBy: SortBy,
  sortOrder: SortOrder,
) => {
  const { data: elements, isLoading } = useListAllElementsMetadata();

  if (!elements) {
    return { elements: [], isLoading };
  }

  const filteredElements = elements.filter((page: ReploElementMetadata) => {
    const nameMatch = page.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    if (selectedFilter === "all") {
      return nameMatch;
    }
    return nameMatch && selectedFilter === page.type;
  });
  const sortedElements = filteredElements.sort((a, b) => {
    let comparison = 0;
    if (sortBy === "name") {
      comparison = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    } else if (sortBy === "createdAt" || sortBy === "updatedAt") {
      const dateA = new Date(a[sortBy]!).getTime();
      const dateB = new Date(b[sortBy]!).getTime();
      comparison = dateA - dateB;
    }

    return sortOrder === "asc" ? comparison : comparison * -1;
  });
  return { elements: sortedElements, isLoading };
};

const LastEditedDisplay: React.FC<{ updatedAt: string; createdAt: string }> = ({
  updatedAt,
  createdAt,
}) => {
  const editedTimeString = useEditedTimeString(
    new Date(updatedAt),
    new Date(createdAt),
  );
  return editedTimeString;
};

const Pages: React.FC<React.PropsWithChildren> = () => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [newPagePopoverOpen, setNewPagePopoverOpen] = React.useState(false);
  const [sortBy, setSortBy] = React.useState<SortBy>("updatedAt");
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("desc");
  const [selectedFilter, setSelectedFilter] = React.useState<
    ReploElementType | "all"
  >("all");
  const { elements, isLoading } = useFilteredPages(
    searchTerm,
    selectedFilter,
    sortBy,
    sortOrder,
  );
  // NOTE (Craig 2025-06-05): Will add in a gallery mode in the future.
  // for now it will only be list mode.
  const [viewMode] = React.useState<ViewMode>("list");
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const { handleCreateElement } = useCreateElement();

  const handleFilterChange = (filter: ReploElementType | "all") => {
    if (selectedFilter === filter) {
      setSelectedFilter("all");
    } else {
      setSelectedFilter(filter);
    }
  };

  const pageTableColumns: Column<ReploElementMetadata & { key: string }>[] = [
    {
      key: "name",
      header: "Name",
      renderCell: (element) => (
        <div className="flex items-center gap-x-2">
          {element.isPublished && (
            <div className="w-2.5 h-2.5 bg-active rounded-full" />
          )}
          <div className="text-default cursor-pointer truncate font-medium">
            {element.name}
          </div>
        </div>
      ),
    },
    {
      key: "lastEdited",
      header: "Last Edited",
      renderCell: (element) => (
        <LastEditedDisplay
          updatedAt={element.updatedAt}
          createdAt={element.createdAt}
        />
      ),
    },
    {
      key: "type",
      header: "Type",
      renderCell: (element) => {
        const {
          icon,
          className: iconClassName,
          label,
        } = exhaustiveSwitch({
          type: element.type,
        })<{
          icon: React.ReactElement;
          className: string;
          label: string;
        }>({
          page: () => ({
            label: "Page",
            icon: <File size={12} />,
            className: "text-page-page bg-page-page-soft",
          }),
          shopifyProductTemplate: () => ({
            label: "Product Template",
            icon: <ShoppingBag size={12} />,
            className: "text-page-template bg-page-template-soft",
          }),
          shopifySection: () => ({
            label: "Section",
            icon: <AlignVerticalSpaceAround size={12} />,
            className: "text-page-section bg-page-section-soft",
          }),
          shopifyArticle: () => ({
            label: "Blog",
            icon: <Newspaper size={12} />,
            className: "text-page-blog bg-page-blog-soft",
          }),
        });
        return (
          <div className="flex gap-2 items-center">
            <div
              className={twMerge(
                "h-5 w-5 flex items-center justify-center rounded-md",
                iconClassName,
              )}
            >
              {icon}
            </div>
            {label}
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "",
      renderCell: (element) => (
        <PageMenu element={element} elements={elements} />
      ),
      align: "right",
    },
  ];

  return (
    <>
      <PageHeader
        title="Pages"
        actions={
          <SplitButton
            buttonText="New Page"
            buttonId="new-page"
            size="base"
            variant="primary"
            startEnhancer={<Plus size={14} />}
            onButtonClick={() =>
              navigate(`/editor/${projectId}/new?elementType=page`)
            }
            popoverProps={{
              isOpen: newPagePopoverOpen,
              onOpenChange: (isOpen: boolean) => setNewPagePopoverOpen(isOpen),
              content: (
                <div className="flex flex-col gap-1 min-w-0">
                  <MenuItem
                    variant="default"
                    size="sm"
                    startEnhancer={<File size={12} />}
                    onClick={() => {
                      void handleCreateElement({ type: "page" });
                      setNewPagePopoverOpen(false);
                    }}
                  >
                    Blank Page
                  </MenuItem>
                  <MenuItem
                    variant="default"
                    size="sm"
                    startEnhancer={<LayoutTemplate size={12} />}
                    onClick={() => {
                      navigate(`/editor/${projectId}/new?elementType=page`);
                    }}
                  >
                    Choose Template
                  </MenuItem>
                </div>
              ),
              className: "w-[150px] p-1.5",
              skipTriggerStyles: true,
              hideCloseButton: true,
              sideOffset: 4,
            }}
          />
        }
      >
        <AnnouncementBanner tags={["New"]} />
      </PageHeader>
      <div className="flex w-full overflow-hidden flex-col gap-y-6 py-6">
        <Input
          placeholder="Search"
          startEnhancer={<Search size={14} />}
          layoutClassName="w-full shrink-0"
          size="base"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="flex items-center flex-shrink-0 gap-x-2">
          <SelectableChip
            size="base"
            onClick={() => handleFilterChange("all")}
            active={selectedFilter === "all"}
          >
            All
          </SelectableChip>
          <SelectableChip
            size="base"
            UNSAFE_activeClassName="text-page-page bg-page-page-soft border-page-page hover:bg-page-page-hover"
            startEnhancer={<File size={12} />}
            onClick={() => handleFilterChange("page")}
            active={selectedFilter === "page"}
          >
            Pages
          </SelectableChip>
          <SelectableChip
            size="base"
            UNSAFE_activeClassName="text-page-template bg-page-template-soft border-page-template hover:bg-page-template-hover"
            startEnhancer={<ShoppingBag size={12} />}
            onClick={() => handleFilterChange("shopifyProductTemplate")}
            active={selectedFilter === "shopifyProductTemplate"}
          >
            Product Templates
          </SelectableChip>
          <SelectableChip
            size="base"
            UNSAFE_activeClassName="text-page-section bg-page-section-soft border-page-section hover:bg-page-section-hover"
            startEnhancer={<AlignVerticalSpaceAround size={12} />}
            onClick={() => handleFilterChange("shopifySection")}
            active={selectedFilter === "shopifySection"}
          >
            Sections
          </SelectableChip>
          <SelectableChip
            size="base"
            UNSAFE_activeClassName="text-page-blog bg-page-blog-soft border-page-blog hover:bg-page-blog-hover"
            startEnhancer={<Newspaper size={12} />}
            onClick={() => handleFilterChange("shopifyArticle")}
            active={selectedFilter === "shopifyArticle"}
          >
            Blogs
          </SelectableChip>
          <div className="flex ml-auto gap-x-2">
            <SortMenu
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          </div>
        </div>
        {viewMode === "gallery" ? (
          <div className="grid grid-cols-[repeat(auto-fill,270px)] gap-6 justify-items-start">
            {elements.map((page: ReploElementMetadata) => (
              <PageCard
                key={page.id}
                pageTitle={page.name}
                lastEditedAt={page.updatedAt}
                type={page.type as ReploElementType}
                isPublished={page.isPublished ?? false}
                createdAt={page.createdAt}
                onClick={() =>
                  navigate(
                    generatePath(routes.editor.element, {
                      projectId: page.projectId,
                      elementId: page.id,
                    }),
                  )
                }
              />
            ))}
          </div>
        ) : (
          <Table
            data={elements.map((element) => ({ ...element, key: element.id }))}
            columns={pageTableColumns}
            isLoading={isLoading}
            layoutClassName="flex-1"
            emptyState={
              <EmptyState
                title="No pages found"
                description="Create a new page to get started."
                size="base"
                variant="primary"
                icon={<File size={16} />}
                UNSAFE_className="py-[15%]"
              >
                <Button
                  variant="link"
                  size="base"
                  onClick={() =>
                    navigate(`/editor/${projectId}/new?elementType=page`)
                  }
                >
                  Create New Page
                </Button>
              </EmptyState>
            }
            onRowClick={(element) => {
              navigate(
                generatePath(routes.editor.element, {
                  projectId: element.projectId,
                  elementId: element.id,
                }),
              );
            }}
          />
        )}
      </div>
    </>
  );
};

const SortMenu: React.FC<{
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;
  sortOrder: SortOrder;
  setSortOrder: (sortOrder: SortOrder) => void;
}> = ({ sortBy, setSortBy, sortOrder, setSortOrder }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const selectedSortByOption = sortByOptions.find(
    (option) => option.value === sortBy,
  );
  const selectedSortOrderOption = sortOrderOptions.find(
    (option) => option.value === sortOrder,
  );

  return (
    <Popover
      side="bottom"
      shouldPreventDefaultOnInteractOutside={false}
      hideCloseButton
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      align="end"
      triggerAsChild
      content={
        <div className="text-default flex w-full flex-col gap-2 text-sm">
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort By</h3>
            {sortByOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                value={option.value}
                selected={sortBy === option.value}
                onClick={() => setSortBy(option.value as SortBy)}
                size="sm"
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
          <Separator />
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort Order</h3>
            {sortOrderOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                value={option.value}
                selected={sortOrder === option.value}
                onClick={() => setSortOrder(option.value as SortOrder)}
                size="sm"
              >
                {sortBy === "name" ? option.label : option.altLabel}
              </MenuItem>
            ))}
          </div>
        </div>
      }
    >
      <Button
        variant="secondary"
        size="base"
        endEnhancer={<ChevronDown size={14} />}
        startEnhancer={selectedSortOrderOption?.startEnhancer}
      >
        {selectedSortByOption?.label}
      </Button>
    </Popover>
  );
};

const PageMenu = ({
  element,
  elements,
}: {
  element: ReploElementMetadata;
  elements: ReploElementMetadata[];
}) => {
  const { handleDuplicateElement } = useDuplicateElement();
  const openModal = useOpenModal();
  const elementTypeSingularName = exhaustiveSwitch({ type: element.type })({
    page: "Page",
    shopifyArticle: "Post",
    shopifyProductTemplate: "Template",
    shopifySection: "Section",
  });
  return (
    <Menu
      items={[
        {
          id: "duplicate",
          type: "leaf",
          variant: "default",
          size: "sm",
          title: `Duplicate ${elementTypeSingularName}`,
          onSelect: () => void handleDuplicateElement(element),
        },
        {
          id: "delete",
          type: "leaf",
          variant: "default",
          size: "sm",
          title: `Delete ${elementTypeSingularName}`,
          onSelect: () => {
            openModal({
              type: "deleteElementConfirmationModal",
              props: {
                type: "delete",
                element,
                elements,
              },
            });
          },
        },
      ]}
      align="end"
      trigger={
        <IconButton
          variant="tertiary"
          size="sm"
          icon={<MoreHorizontal size={16} className="text-muted" />}
        />
      }
    />
  );
};

export default Pages;
